'use client';

import { useState, useEffect } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import {
  Eye,
  MessageCircle,
  Flame,
  BookOpen,
  TrendingUp,
  Edit3,
  Images,
  Clock,
  Calendar,
  MapPin,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { formatDate, formatLifeSpan } from '@/lib/utils';

interface Memorial {
  id: string;
  slug: string;
  firstName: string | null;
  lastName: string | null;
  birthName: string | null;
  dateOfBirth: string | null;
  dateOfDeath: string | null;
  city: string | null;
  placeOfResidence: string | null;
  imagePath: string | null;
  condolenceComments?: Array<{
    id: string;
    firstName: string | null;
    lastName: string | null;
    descriptions: string | null;
    isPublic: boolean;
    createdAt: string;
    replies?: Array<unknown>;
  }>;
  candles?: Array<{ id: string }>;
  memories?: Array<{ id: string }>;
  galleryImages?: Array<{ id: string; isReleased: boolean }>;
  institutions?: Array<{ id: string }>;
}

export default function FamilyDashboardPage() {
  const params = useParams();
  const slug = params.slug as string;
  const [memorial, setMemorial] = useState<Memorial | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMemorial = async () => {
      try {
        // Use family API to work with unpublished memorials
        const res = await fetch(`/api/family/${slug}`);
        if (!res.ok) throw new Error('Memorial not found');
        const data = await res.json();
        setMemorial(data);
      } catch (err) {
        console.error(err);
        setError('Gedenkseite nicht gefunden');
      } finally {
        setLoading(false);
      }
    };
    fetchMemorial();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-memorial-primary" />
      </div>
    );
  }

  if (error || !memorial) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-serif text-gray-900">{error || 'Gedenkseite nicht gefunden'}</h1>
      </div>
    );
  }

  const fullName = `${memorial.firstName || ''} ${memorial.lastName || ''}`.trim();
  const lifeSpan = formatLifeSpan(memorial.dateOfBirth, memorial.dateOfDeath);

  // Statistics from actual data
  const stats = [
    { name: 'Seitenaufrufe', value: '—', change: '', icon: Eye },
    { name: 'Kondolenzen', value: String(memorial.condolenceComments?.length || 0), change: '', icon: MessageCircle },
    { name: 'Kerzen', value: String(memorial.candles?.length || 0), change: '', icon: Flame },
    { name: 'Erinnerungen', value: String(memorial.memories?.length || 0), change: '', icon: BookOpen },
  ];

  // Quick actions
  const quickActions = [
    { name: 'Gedenkseite bearbeiten', href: `/family/${slug}/edit`, icon: Edit3, color: 'bg-blue-500' },
    { name: 'Fotos verwalten', href: `/family/${slug}/gallery`, icon: Images, color: 'bg-purple-500' },
    { name: 'Kondolenzen beantworten', href: `/family/${slug}/condolences`, icon: MessageCircle, color: 'bg-green-500' },
    { name: 'Lebenslauf erweitern', href: `/family/${slug}/timeline`, icon: Clock, color: 'bg-amber-500' },
  ];

  // Recent condolences
  const recentCondolences = memorial.condolenceComments?.filter(c => c.isPublic).slice(0, 3) || [];

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Verwalten Sie die Gedenkseite von {fullName}</p>
      </div>

      {/* Memorial summary card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="md:flex">
          {/* Image */}
          <div className="relative w-full md:w-48 h-48 md:h-auto bg-gray-100 flex-shrink-0">
            {memorial.imagePath ? (
              <Image
                src={memorial.imagePath}
                alt={fullName}
                fill
                className="object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-4xl font-serif text-gray-400">
                  {memorial.firstName?.charAt(0)}{memorial.lastName?.charAt(0)}
                </span>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="p-6 flex-1">
            <h2 className="text-xl font-serif text-gray-900">{fullName}</h2>
            {memorial.birthName && (
              <p className="text-sm text-gray-500">geb. {memorial.birthName}</p>
            )}
            <p className="text-memorial-secondary font-medium mt-1">{lifeSpan}</p>

            <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
              {memorial.city && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {memorial.placeOfResidence || memorial.city}
                </div>
              )}
              {memorial.dateOfDeath && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Verstorben am {formatDate(memorial.dateOfDeath)}
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                Veröffentlicht
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {memorial.galleryImages?.filter(img => img.isReleased).length || 0} Fotos
              </span>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                {memorial.institutions?.length || 0} Institutionen
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-lg bg-memorial-primary/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-memorial-primary" />
                </div>
                {stat.change && (
                  <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                    <TrendingUp className="w-3 h-3" />
                    {stat.change}
                  </span>
                )}
              </div>
              <p className="mt-3 text-2xl font-semibold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.name}</p>
            </div>
          );
        })}
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">Schnellaktionen</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.name}
                href={action.href}
                className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow group"
              >
                <div className={`w-12 h-12 rounded-lg ${action.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 group-hover:text-memorial-primary transition-colors">
                    {action.name}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent condolences */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Neueste Kondolenzen</h2>
          <Link
            href={`/family/${slug}/condolences`}
            className="text-sm text-memorial-primary hover:text-memorial-secondary flex items-center gap-1"
          >
            Alle ansehen
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {recentCondolences.length > 0 ? (
          <div className="space-y-3">
            {recentCondolences.map((condolence) => (
              <div
                key={condolence.id}
                className="bg-white rounded-xl border border-gray-200 p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-memorial-secondary/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-memorial-secondary text-sm font-medium">
                      {condolence.firstName?.charAt(0)}{condolence.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-gray-900">
                        {condolence.firstName} {condolence.lastName}
                      </p>
                      {condolence.createdAt && (
                        <p className="text-xs text-gray-500">
                          {formatDate(condolence.createdAt, 'd. MMM yyyy')}
                        </p>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {condolence.descriptions}
                    </p>
                    {(!condolence.replies || condolence.replies.length === 0) && (
                      <button className="mt-2 text-sm text-memorial-primary hover:text-memorial-secondary font-medium">
                        Antworten
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Noch keine Kondolenzen vorhanden</p>
          </div>
        )}
      </div>
    </div>
  );
}
